import { useState, useEffect, useCallback } from 'react';
import { useMultipleWeather } from '../hooks';
import { formatTemperatureFromCelsius } from '../utils/format';
import { useSettingsStore } from '../store/settings';
import './ForecastCarousel.css';

interface ForecastLocation {
    code: string;
    label: string;
    locationId: number;
    region: string;
}

// Global coverage: North America, Europe, Asia, Middle East, Oceania, South America, Africa
const CAROUSEL_LOCATIONS: ForecastLocation[] = [
    // North America
    { code: 'KJFK', label: 'JFK', locationId: 1001, region: 'NA' },
    { code: 'KLAX', label: 'LAX', locationId: 1002, region: 'NA' },
    { code: 'KORD', label: 'ORD', locationId: 1003, region: 'NA' },
    { code: 'KMIA', label: 'MIA', locationId: 1004, region: 'NA' },
    { code: 'CYYZ', label: 'YYZ', locationId: 1005, region: 'NA' },
    { code: 'KATL', label: 'ATL', locationId: 1006, region: 'NA' },
    { code: 'KBOS', label: 'BOS', locationId: 1007, region: 'NA' },
    { code: 'KDFW', label: 'DFW', locationId: 1008, region: 'NA' },
    { code: 'KIAH', label: 'IAH', locationId: 1009, region: 'NA' },
    { code: 'KSEA', label: 'SEA', locationId: 1010, region: 'NA' },
    { code: 'KLAS', label: 'LAS', locationId: 1011, region: 'NA' },
    { code: 'KDCA', label: 'DCA', locationId: 1012, region: 'NA' },
    // Europe
    { code: 'EGLL', label: 'LHR', locationId: 1020, region: 'EU' },
    { code: 'LFPG', label: 'CDG', locationId: 1021, region: 'EU' },
    { code: 'EDDF', label: 'FRA', locationId: 1022, region: 'EU' },
    { code: 'EHAM', label: 'AMS', locationId: 1023, region: 'EU' },
    { code: 'LEMD', label: 'MAD', locationId: 1024, region: 'EU' },
    { code: 'EDDB', label: 'BER', locationId: 1025, region: 'EU' },
    // Asia Pacific
    { code: 'RJTT', label: 'HND', locationId: 1030, region: 'AS' },
    { code: 'WSSS', label: 'SIN', locationId: 1031, region: 'AS' },
    { code: 'VHHH', label: 'HKG', locationId: 1032, region: 'AS' },
    { code: 'RKSI', label: 'ICN', locationId: 1033, region: 'AS' },
    { code: 'YSSY', label: 'SYD', locationId: 1034, region: 'OC' },
    // Middle East
    { code: 'OMDB', label: 'DXB', locationId: 1040, region: 'ME' },
    { code: 'OTHH', label: 'DOH', locationId: 1041, region: 'ME' },
];

const ROTATION_INTERVAL = 3000; // 3 seconds per location for faster global coverage

function ForecastCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const { temperatureUnit } = useSettingsStore();

    const locationIds = CAROUSEL_LOCATIONS.map(loc => loc.locationId);
    const { data: weatherData } = useMultipleWeather(locationIds);

    // Auto-rotate carousel
    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % CAROUSEL_LOCATIONS.length);
                setIsAnimating(false);
            }, 300);
        }, ROTATION_INTERVAL);

        return () => clearInterval(interval);
    }, [isPaused]);

    // Manual navigation
    const goToIndex = useCallback((index: number) => {
        if (index === currentIndex) return;
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex(index);
            setIsAnimating(false);
        }, 200);
    }, [currentIndex]);

    const handlePrevious = useCallback(() => {
        const newIndex = (currentIndex - 1 + CAROUSEL_LOCATIONS.length) % CAROUSEL_LOCATIONS.length;
        goToIndex(newIndex);
    }, [currentIndex, goToIndex]);

    const handleNext = useCallback(() => {
        const newIndex = (currentIndex + 1) % CAROUSEL_LOCATIONS.length;
        goToIndex(newIndex);
    }, [currentIndex, goToIndex]);

    const currentLocation = CAROUSEL_LOCATIONS[currentIndex];
    const currentWeather = weatherData?.find(w => w.locationId === currentLocation.locationId);
    const temperature = currentWeather ? formatTemperatureFromCelsius(currentWeather.temperature, temperatureUnit) : '--°';

    // Region labels for display
    const regionLabels: Record<string, string> = {
        'NA': '🌎',
        'EU': '🌍',
        'AS': '🌏',
        'ME': '🏜️',
        'OC': '🌊',
        'SA': '🌎',
        'AF': '🌍',
    };


    // Direct fetch temperatures for ticker display
    const [tickerTemps, setTickerTemps] = useState<Record<string, string>>({});
    useEffect(() => {
        async function fetchTemps() {
            const temps: Record<string, string> = {};
            // Batch fetch from Whether forecast API first
            const promises = CAROUSEL_LOCATIONS.map(async (loc) => {
                try {
                    // Try Whether API first
                    const r = await fetch('/api/forecast?location=' + loc.code + '&date=' + new Date().toISOString().split('T')[0]);
                    const d = await r.json();
                    if (d.point_forecast?.t_max) {
                        const tF = d.point_forecast.t_max;
                        const t = temperatureUnit === 'C' ? Math.round((tF - 32) * 5/9) : Math.round(tF);
                        temps[loc.code] = t + (temperatureUnit === 'C' ? '\u00b0C' : '\u00b0F');
                        return;
                    }
                } catch {}
                // Fallback: Open-Meteo free API (no key needed, uses lat/lon)
                try {
                    const lat = loc.locationId; // We'll use a lookup
                    const coords: Record<string, [number, number]> = {
                        'KLAX': [33.94, -118.41], 'EDDF': [50.03, 8.57], 'EHAM': [52.31, 4.77],
                        'LEMD': [40.47, -3.56], 'EDDB': [52.37, 13.50], 'WSSS': [1.36, 103.99],
                        'VHHH': [22.31, 113.91], 'YSSY': [-33.95, 151.18], 'OMDB': [25.25, 55.36],
                        'OTHH': [25.27, 51.61], 'KDFW': [32.90, -97.04], 'KIAH': [29.98, -95.34],
                        'KSEA': [47.45, -122.31], 'YMML': [-37.67, 144.84],
                        'KCVG': [39.05, -84.67], 'KPHL': [39.87, -75.24],
                    };
                    const c = coords[loc.code];
                    if (c) {
                        const r2 = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${c[0]}&longitude=${c[1]}&current=temperature_2m&temperature_unit=${temperatureUnit === 'C' ? 'celsius' : 'fahrenheit'}`);
                        const d2 = await r2.json();
                        if (d2.current?.temperature_2m != null) {
                            temps[loc.code] = Math.round(d2.current.temperature_2m) + (temperatureUnit === 'C' ? '\u00b0C' : '\u00b0F');
                        }
                    }
                } catch {}
            });
            await Promise.all(promises);
            setTickerTemps(temps);
        }
        fetchTemps();
        const iv = setInterval(fetchTemps, 300000);
        return () => clearInterval(iv);
    }, [temperatureUnit]);

    // Build ticker items for ALL locations
    const tickerItems = CAROUSEL_LOCATIONS.map((loc) => {
        const wd = weatherData?.find(w => w.locationId === loc.locationId);
        const temp = tickerTemps[loc.code] || (wd ? formatTemperatureFromCelsius(wd.temperature, temperatureUnit) : '--\u00b0');
        return { label: loc.label, temp, region: loc.region };
    });

    return (
        <div className="forecast-carousel ticker-mode"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div className={`ticker-track ${isPaused ? 'paused' : ''}`}>
                {/* Duplicate items for seamless loop */}
                {[...tickerItems, ...tickerItems].map((item, i) => (
                    <div key={i} className="ticker-item">
                        <span className="ticker-label">{item.label}</span>
                        <span className="ticker-temp">{item.temp}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ForecastCarousel;
